import { useEffect } from "react";

type SEOHeadProps = {
  title: string;
  description?: string;
  canonical?: string;
  noIndex?: boolean;
  schemaJson?: Record<string, unknown>;
};

/**
 * Route metadata is owned by TanStack Start's root head. This client-side
 * compatibility component keeps legacy page declarations harmless while
 * preserving the document title and structured-data updates after navigation.
 */
export function SEOHead({
  title,
  description,
  canonical,
  schemaJson,
}: SEOHeadProps) {
  useEffect(() => {
    document.title = title;

    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute("content", description);
    }

    if (canonical) {
      let linkCanonical = document.querySelector('link[rel="canonical"]');
      if (!linkCanonical) {
        linkCanonical = document.createElement("link");
        linkCanonical.setAttribute("rel", "canonical");
        document.head.appendChild(linkCanonical);
      }
      linkCanonical.setAttribute(
        "href",
        canonical.startsWith("http")
          ? canonical
          : `https://crecelocal.com${canonical}`,
      );
    }

    if (!schemaJson) return;
    const existing = document.querySelector('script[data-schema="seo"]');
    if (existing) {
      existing.textContent = JSON.stringify(schemaJson);
      return;
    }
    const node = document.createElement("script");
    node.type = "application/ld+json";
    node.setAttribute("data-schema", "seo");
    node.text = JSON.stringify(schemaJson);
    document.head.appendChild(node);
    return () => node.remove();
  }, [canonical, description, schemaJson, title]);

  return null;
}
